import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X,
  Bell,
  User,
  LogOut,
  LayoutDashboard,
  Briefcase,
  FileText,
  CheckCircle,
  Key,
  Award,
  AlertTriangle,
  Bookmark,
  Calendar,
  Crown,
  Home,
  Landmark,
  Search,
  LogIn
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import SiteLogo from '../common/SiteLogo';

const Header = ({ onOpenSearch }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { isAuthenticated, isAdmin, user, adminData, logout, isLoading } = useAuth();
  const { openLoginModal } = useAuthModal();
  const location = useLocation();
  const navigate = useNavigate();

  // Public navigation
  const publicNav = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Jobs Notification', href: '/jobs', icon: Briefcase },
    { name: 'Admit Cards', href: '/admit-cards', icon: FileText },
    { name: 'Results', href: '/results', icon: CheckCircle },
    { name: 'Answer Key', href: '/answer-key', icon: Key },
    { name: 'Exams Calendar', href: '/exams-calendar', icon: Calendar },
    { name: 'Scholarships', href: '/scholarships', icon: Award },
    { name: 'Sarkari Yojana', href: '/sarkari-yojana', icon: Landmark },
  ];

  // Admin navigation
  const adminNav = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Jobs', href: '/admin/jobs', icon: Briefcase },
    { name: 'Admit Cards', href: '/admin/admit-cards', icon: FileText },
    { name: 'Results', href: '/admin/results', icon: CheckCircle },
    { name: 'Answer Keys', href: '/admin/answer-keys', icon: Key },
    { name: 'Scholarships', href: '/admin/scholarships', icon: Award },
    { name: 'Yojana', href: '/admin/yojana', icon: Landmark },
    { name: 'Exam Calendar', href: '/admin/exam-calendar', icon: Calendar },
    { name: 'Expired', href: '/admin/expired', icon: AlertTriangle },
  ];

  const isActive = (path) => location.pathname === path;

  // ONE nav link recipe for both bars. The public and admin arrays used to
  // disagree on size (11.5/12px vs 13px), padding (py-1 vs py-1.5) and radius
  // (rounded-md vs rounded-sm) for what is the same control. Geometry, type and
  // radius are shared here; only the resting/active colours differ below.
  // Sizing & Geometry: Compact, high-density, sharp header controls
  const NAV_LINK =
    'inline-flex items-center h-7 px-2.5 rounded-md font-heading text-[11px] font-semibold whitespace-nowrap transition-all duration-150';

  // Drawer rows are the most-tapped controls on a phone, so they carry the
  // 44px minimum rather than the 35px `py-2` used to give them.
  const DRAWER_LINK =
    'flex items-center gap-3 px-3 min-h-[44px] rounded-md font-heading text-xs font-bold transition-colors';

  const handleLogout = () => {
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
    logout();
    if (location.pathname.startsWith('/admin')) {
      navigate('/admin/login');
    } else {
      navigate('/');
    }
  };

  // ==================== ADMIN HEADER ====================
  const isAdminRoute = location.pathname.startsWith('/admin');
  if (isAdmin && isAdminRoute) {
    return (
      <>
        {/* Top Saffron/Gold Accent Line */}
        <div className="h-1 bg-gov-saffron"></div>

        {/* The bottom rule was #1a3a60 - navy on navy, i.e. invisible. A saffron
            edge bookends the bar with the accent line above it and reads as
            identity. Height stays 64px + the two accent rules. */}
        <header className="bg-gov-navy text-white shadow-md border-b-2 border-gov-saffron sticky top-0 z-40">
          <div className="max-w-[1400px] mx-auto px-4">
            <div className="flex items-center h-16">
              {/* Far Left: Admin Title */}
              <Link to="/admin/dashboard" className="flex items-center space-x-2.5 flex-shrink-0 group py-1">
              <SiteLogo
                alt="Sarkari Adda Admin"
                priority
                className="w-10 h-10 md:w-12 md:h-12 max-w-[40px] max-h-[40px] md:max-w-[48px] md:max-h-[48px] object-contain rounded-sm flex-shrink-0"
              />
                <span className="text-base md:text-lg font-sans font-black tracking-wider uppercase">
                  {/* amber-400, not gov-saffron: #d97706 on navy is 4.1:1, and
                      this word has to stay legible on the dark bar. */}
                  <span className="text-amber-400">Admin</span> <span className="text-white">Panel</span>
                </span>
              </Link>

              {/* Far Right: Nav + Logout */}
              <div className="hidden lg:flex items-center gap-2.5 ml-auto">
                <nav className="flex items-center space-x-1">
                  {adminNav.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`${NAV_LINK} ${
                          active
                            ? 'bg-gov-saffron text-white shadow-xs'
                            : 'text-slate-100 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>

                <div className="h-4 w-px bg-slate-600/60 mx-1"></div>

                <button
                  onClick={handleLogout}
                  className="inline-flex items-center justify-center gap-1.5 h-8 px-3 bg-red-600 hover:bg-red-700 border border-red-500 rounded-md text-2xs text-white font-bold transition-all active:scale-97 whitespace-nowrap cursor-pointer"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Logout</span>
                </button>
              </div>

              {/* Admin Mobile Menu */}
              <div className="lg:hidden ml-auto">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="flex items-center justify-center w-11 h-11 -mr-2 rounded-md text-slate-200 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                  aria-label="Toggle mobile menu"
                >
                  {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>
          </div>

          {/* Admin Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute right-4 top-[66px] w-[300px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-96px)] overflow-y-auto bg-gov-navy text-white shadow-2xl rounded-xl border border-gov-navy-hover p-3 z-50 animate-menu-in">
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-0.5">
                  {adminNav.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`${DRAWER_LINK} ${
                          active
                            ? 'bg-gov-navy-hover text-white border-l-4 border-amber-400'
                            : 'text-slate-200 hover:bg-gov-navy-hover hover:text-white'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon className="w-4 h-4 text-amber-400 shrink-0" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
                <hr className="border-gov-navy-hover" />
                <button
                  onClick={handleLogout}
                  className={`${DRAWER_LINK} w-full text-red-400 hover:bg-red-950/30 text-left cursor-pointer`}
                >
                  <LogOut className="w-4 h-4 text-red-400 shrink-0" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </header>
      </>
    );
  }

  // ==================== PUBLIC HEADER ====================
  return (
    <>
      {/* Top Saffron Accent Line */}
      <div className="h-1 bg-gov-saffron"></div>

      {/* TypingHub-style glassmorphic header with 2.5px accent bottom border */}
      <header className="bg-white/95 backdrop-blur-md text-gov-body shadow-[0_2px_10px_rgba(0,0,0,0.03)] border-b-[2.5px] border-[#2563eb] sticky top-0 z-40">
        <div className="max-w-[1480px] mx-auto px-4 md:px-6">
          <div className="flex items-center h-16 justify-between">
            {/* Far Left: Logo */}
            <Link to="/" className="flex items-center flex-shrink-0 group py-0.5" title="Sarkari Adda 24x7">
              <SiteLogo
                alt="Sarkari Adda 24x7 Logo"
                priority
                className="h-12 md:h-[54px] w-auto max-h-[54px] object-contain flex-shrink-0 drop-shadow-xs group-hover:scale-102 transition-transform"
              />
            </Link>

            {/* Far Right: Nav + Go Premium + User */}
            <div className="hidden lg:flex items-center gap-2 ml-auto">
              <nav className="flex items-center space-x-1 md:space-x-1.5">
                {publicNav.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center gap-1 h-7 px-1.5 font-heading text-[11.5px] whitespace-nowrap transition-colors duration-150 group ${
                        active
                          ? 'text-[#2563eb] font-bold'
                          : 'text-[#334155] hover:text-[#2563eb] font-semibold'
                      }`}
                    >
                      {Icon && (
                        <Icon 
                          className={`w-3.5 h-3.5 shrink-0 transition-colors ${
                            active ? 'text-[#2563eb]' : 'text-[#64748b] group-hover:text-[#2563eb]'
                          }`} 
                        />
                      )}
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="h-4 w-px bg-slate-200 mx-0.5"></div>

              {/* Standalone Luxury Search Icon */}
              <button
                type="button"
                onClick={onOpenSearch}
                aria-label="Search jobs, results and admit cards"
                title="Search"
                className="flex items-center justify-center w-7 h-7 text-gov-body hover:text-gov-saffron hover:bg-amber-500/10 rounded transition-all duration-150 ease-ui cursor-pointer shrink-0 active:scale-95 group"
              >
                <Search className="w-3.5 h-3.5 group-hover:text-gov-saffron transition-colors" strokeWidth={2.3} />
              </button>

              {/* Luxury Compact Go Premium Button */}
              <Link
                to="/premium-plans"
                className={`inline-flex items-center gap-1 h-[26px] px-2 rounded font-heading text-[10.5px] font-bold transition-all duration-150 whitespace-nowrap active:scale-95 ${
                  user?.isPremium
                    ? 'bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 shadow-2xs'
                    : 'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-600 hover:to-orange-600 text-white border border-amber-400/50 shadow-xs hover:shadow-[0_2px_8px_rgba(245,158,11,0.35)] hover:scale-102'
                }`}
                aria-label="Go Premium Plans"
              >
                <Crown className="w-3 h-3 text-amber-200" />
                <span>{user?.isPremium ? 'Premium' : 'Go Premium'}</span>
              </Link>

              {isLoading ? (
                <div className="w-10 h-[26px] bg-slate-200 animate-pulse rounded flex-shrink-0" />
              ) : isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="inline-flex items-center gap-1 h-[26px] px-2 bg-white hover:bg-slate-50 border border-slate-300/90 hover:border-slate-400 rounded text-slate-800 transition-all duration-150 cursor-pointer font-heading text-[10.5px] font-bold shadow-2xs active:scale-95"
                  >
                    <User className="w-3 h-3 text-blue-600 flex-shrink-0" />
                    <span className="max-w-[80px] truncate">
                      {isAdmin ? (adminData?.name || 'Admin') : (user?.name || 'Account')}
                    </span>
                  </button>
                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                      <div className="absolute right-0 top-full mt-2 bg-white text-slate-800 rounded-xl shadow-xl border border-slate-200/90 z-50 w-64 overflow-hidden animate-menu-in origin-top-right">
                        
                        {/* User Info Header with Navy styling */}
                        <div className="px-3.5 py-2.5 bg-gov-navy text-white flex items-center justify-between border-b border-gov-line">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-blue-600 to-indigo-600 border border-blue-400/40 text-white font-bold flex items-center justify-center text-xs uppercase shadow-2xs shrink-0">
                              {(isAdmin ? adminData?.name : user?.name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-bold text-white font-heading truncate">
                                {isAdmin ? (adminData?.name || 'Admin') : (user?.name || 'Candidate')}
                              </p>
                              <p className="text-[10px] text-slate-300 truncate">
                                {isAdmin ? (adminData?.email || '') : (user?.email || '')}
                              </p>
                            </div>
                          </div>
                          {user?.isPremium && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/40 text-[9px] font-extrabold uppercase flex items-center gap-1 shrink-0">
                              <Crown className="w-2.5 h-2.5 text-amber-300" /> VIP
                            </span>
                          )}
                        </div>

                        {/* Menu Items */}
                        <div className="p-1.5 space-y-0.5 font-heading">
                          {isAdmin ? (
                            <Link
                              to="/admin/dashboard"
                              onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-slate-700 hover:text-blue-700 hover:bg-blue-50/80 rounded-lg transition-colors"
                            >
                              <LayoutDashboard className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                              <span>Admin Panel</span>
                            </Link>
                          ) : (
                            <>
                              <Link
                                to="/dashboard"
                                onClick={() => setShowUserMenu(false)}
                                className="flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-slate-700 hover:text-blue-700 hover:bg-blue-50/80 rounded-lg transition-colors"
                              >
                                <LayoutDashboard className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                                <span>My Dashboard</span>
                              </Link>
                              <Link
                                to="/premium-plans"
                                onClick={() => setShowUserMenu(false)}
                                className="flex items-center justify-between px-3 py-2 text-xs font-bold text-slate-700 hover:text-amber-700 hover:bg-amber-50/80 rounded-lg transition-colors"
                              >
                                <div className="flex items-center gap-2.5">
                                  <Crown className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                  <span>Go Premium</span>
                                </div>
                                {!user?.isPremium && (
                                  <span className="text-[9.5px] px-1.5 py-0.2 bg-amber-100 text-amber-800 rounded font-extrabold">
                                    Get VIP
                                  </span>
                                )}
                              </Link>
                              <Link
                                to="/smart-alerts"
                                onClick={() => setShowUserMenu(false)}
                                className="flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/80 rounded-lg transition-colors"
                              >
                                <Bell className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                <span>Manage Alerts</span>
                              </Link>
                              <Link
                                to="/bookmarks"
                                onClick={() => setShowUserMenu(false)}
                                className="flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-slate-700 hover:text-purple-700 hover:bg-purple-50/80 rounded-lg transition-colors"
                              >
                                <Bookmark className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                                <span>Saved Jobs</span>
                              </Link>
                            </>
                          )}

                          <div className="border-t border-slate-100 my-1"></div>

                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-lg text-left transition-colors cursor-pointer"
                          >
                            <LogOut className="w-3.5 h-3.5 shrink-0 text-red-500" />
                            <span>Logout</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={openLoginModal}
                  className="inline-flex items-center gap-1 h-[26px] px-2 rounded font-heading text-[10.5px] font-bold text-white bg-gradient-to-r from-[#0f172a] via-[#1e3a8a] to-[#2563eb] hover:from-[#1e293b] hover:to-[#1d4ed8] border border-blue-900/40 shadow-xs hover:shadow-[0_2px_8px_rgba(37,99,235,0.3)] transition-all duration-150 cursor-pointer active:scale-95 hover:scale-102"
                >
                  <LogIn className="w-3 h-3 text-blue-300" />
                  <span>Login</span>
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden ml-auto">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center justify-center w-11 h-11 -mr-2 rounded-md text-gov-body hover:text-gov-ink hover:bg-slate-100 transition-colors cursor-pointer"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute right-4 top-[66px] w-[300px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-96px)] overflow-y-auto bg-white text-gov-ink shadow-2xl rounded-xl border border-gov-line p-3 z-50 animate-menu-in">
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-0.5">
                {publicNav.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${DRAWER_LINK} ${
                        active
                          ? 'bg-gov-navy text-white'
                          : 'text-gov-body hover:bg-slate-100 hover:text-gov-ink'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-amber-400' : 'text-gov-saffron'}`} />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </div>

              <hr className="border-gov-line" />
              
              <div className="flex flex-col gap-1">
                <Link
                  to="/premium-plans"
                  className={`${DRAWER_LINK} shadow-xs ${
                    user?.isPremium
                      ? 'bg-amber-100 text-amber-900 border border-amber-300'
                      : 'bg-gradient-to-r from-gov-saffron to-orange-600 hover:from-gov-saffron-hover hover:to-orange-700 text-white'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Crown className={`w-4 h-4 shrink-0 ${user?.isPremium ? 'text-amber-700' : 'text-amber-200'}`} />
                  <span>{user?.isPremium ? 'Premium Active' : 'Upgrade to Premium'}</span>
                </Link>

                {isLoading ? (
                  <div className="h-11 w-full bg-slate-100 animate-pulse rounded-md" />
                ) : isAuthenticated ? (
                  <>
                    {isAdmin ? (
                      <Link
                        to="/admin/dashboard"
                        className={`${DRAWER_LINK} text-white bg-gov-navy hover:bg-gov-navy-hover`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4 text-amber-400 shrink-0" />
                        <span>Admin Panel</span>
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/dashboard"
                          className={`${DRAWER_LINK} text-gov-body hover:bg-slate-100 hover:text-gov-ink`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <LayoutDashboard className="w-4 h-4 shrink-0" />
                          <span>My Dashboard</span>
                        </Link>
                        <Link
                          to="/bookmarks"
                          className={`${DRAWER_LINK} text-gov-body hover:bg-slate-100 hover:text-gov-ink`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Bookmark className="w-4 h-4 shrink-0" />
                          <span>Saved Jobs</span>
                        </Link>
                      </>
                    )}
                    <button
                      onClick={handleLogout}
                      className={`${DRAWER_LINK} w-full text-red-600 hover:bg-red-50 text-left cursor-pointer`}
                    >
                      <LogOut className="w-4 h-4 shrink-0" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  /* h-ctl-lg is doing the real work here - see bugsFound:
                     `.sa-btn-primary` is declared after `.sa-btn-lg` in
                     index.css and re-inlines `.sa-btn`'s 38px height at equal
                     specificity, so `sa-btn-lg` alone renders 38px. */
                  <button
                    onClick={() => {
                      openLoginModal();
                      setIsMobileMenuOpen(false);
                    }}
                    className="inline-flex items-center justify-center gap-2 h-ctl-lg w-full mt-1 rounded-lg font-heading text-sm font-bold text-white bg-gradient-to-r from-[#0f172a] via-[#1e3a8a] to-[#2563eb] hover:from-[#1e293b] hover:to-[#1d4ed8] border border-blue-900/40 shadow-md transition-all duration-150 cursor-pointer active:scale-98"
                  >
                    <LogIn className="w-4 h-4 text-blue-300 shrink-0" />
                    <span>Login to Account</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
